import {Component, computed, inject, signal} from '@angular/core';
import {BaseModalComponent} from "../../../../../../core/components/modals/base-modal/base-modal.component";
import {qualityControlApprovalsResourceType} from "./configs/quality-control-approvals-config";
import {MAT_DIALOG_DATA} from "@angular/material/dialog";
import {QualityControlApprovalsData, QualityControlApprovalsDialogData} from "./types/quality-control-approvals.types";
import {QualityControlApprovalsService} from "./services/quality-control-approvals.service";
import {FormGroup} from "@angular/forms";
import {ProductCardFormFieldService} from "../../../services/product-card-form-field.service";
import {qualityControlApprovalsFormFields} from "./configs/quality-control-approvals-form-fields";
import {QualityControlApprovalsFormField} from "./types/quality-control-approvals-form-field.types";
import {defaultQualityControlApprovalsData} from "./configs/default-quality-control-approvals-data";
import {
  MatAccordion,
  MatExpansionPanel,
  MatExpansionPanelHeader,
  MatExpansionPanelTitle
} from "@angular/material/expansion";
import {MatButton} from "@angular/material/button";
import {
  QualityControlApprovalsTitleComponent
} from "./components/quality-control-approvals-title/quality-control-approvals-title.component";
import {
  QualityControlApprovalsFormComponent
} from "./components/quality-control-approvals-form/quality-control-approvals-form.component";
import {SelectData} from "../../../../../../core/types/records-table.types";
import {EditModeService} from "../../../../../../core/services/edit-mode.service";
import {LoaderComponent} from "../../../../../../shared/components/loader/loader.component";

@Component({
  selector: 'app-quality-control-approvals-modal',
  imports: [
    BaseModalComponent,
    MatAccordion,
    MatButton,
    MatExpansionPanel,
    MatExpansionPanelHeader,
    MatExpansionPanelTitle,
    QualityControlApprovalsTitleComponent,
    QualityControlApprovalsFormComponent,
    LoaderComponent
  ],
  templateUrl: './quality-control-approvals-modal.component.html',
  styleUrl: './quality-control-approvals-modal.component.scss',
  providers: [ EditModeService ]
})
export class QualityControlApprovalsModalComponent extends BaseModalComponent {
  private qualityControlApprovalsDialogData = inject<QualityControlApprovalsDialogData>(MAT_DIALOG_DATA);
  private qualityControlApprovalsService: QualityControlApprovalsService = inject(QualityControlApprovalsService);
  private productCardFormFieldService: ProductCardFormFieldService = inject(ProductCardFormFieldService);

  public readonly qualityControlApprovalsResourceType = qualityControlApprovalsResourceType;
  public qualityControlApprovalsFormGroups = signal<Array<FormGroup>>([]);
  public qualityControlApprovalsData = signal<Array<QualityControlApprovalsData>>([]);
  public approvalStatusSelectData = signal<Array<SelectData>>([]);
  public isValid = signal<boolean>(false);
  public noFirstData = signal<boolean>(false);
  public productCardQualityControlId = computed(() => this.qualityControlApprovalsDialogData.parentId);

  public override async ngOnInit(): Promise<void> {
    super.ngOnInit();
    await this.initModal();
  }

  public override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.modalStatesService.closeModal(qualityControlApprovalsResourceType.key);
  }

  public handleAddNew(): void {
    const formGroup = this.baseModalService.getFormGroup<QualityControlApprovalsFormField>(qualityControlApprovalsFormFields);
    this.qualityControlApprovalsFormGroups.update(forms  => [ ...forms, formGroup ]);
    this.qualityControlApprovalsData.update(data  => [ ...data, defaultQualityControlApprovalsData ]);
    this.isValid.set(false);
    this.noFirstData.set(!!this.qualityControlApprovalsData()?.at(0)?.id);
  }

  public async handleDelete(event: MouseEvent, id: number, index: number): Promise<void> {
    event.stopPropagation();
    const isConfirmed = await this.productCardFormFieldService.isDeleteConfirmed(id);
    if (!isConfirmed) { return; }

    this.loadingService.setIsLoadingState(true);
    await this.deleteRecord(qualityControlApprovalsResourceType.path, id)
      .then(() => this.removeFactoryBankDetailsAtIndex(index))
      .finally(() => this.loadingService.setIsLoadingState(false));
  }

  public updateData(data: QualityControlApprovalsData, index: number): void {
    this.qualityControlApprovalsData()[index] = data;
    this.qualityControlApprovalsFormGroups().at(index)?.patchValue(data);
    this.isValid.set(!!data.id);
  }

  public deleteData(index: number): void {
    this.qualityControlApprovalsFormGroups.update(data => {
      const newData = [...data];
      newData.splice(index, 1);
      return newData;
    });

    this.qualityControlApprovalsData.update(groups => {
      const newGroups = [...groups];
      newGroups.splice(index, 1);
      return newGroups;
    });

    this.isValid.set(true);
  }

  public setLoadingState(isLoading: boolean): void {
    this.loadingService.setIsLoadingState(isLoading);
  }

  private async initModal(): Promise<void> {
    this.loadingService.setIsLoadingState(true);
    this.modalStatesService.openModal(qualityControlApprovalsResourceType.key);
    await this.qualityControlApprovalsService.getSelectData()
      .then(data => this.approvalStatusSelectData.set(data));
    await this.qualityControlApprovalsService.getDataInModal<QualityControlApprovalsData>(this.productCardQualityControlId())
      .then(data => this.successLoadData(data))
      .finally(() => this.loadingService.setIsLoadingState(false));
  }

  private successLoadData(data: Array<QualityControlApprovalsData>): void {
    this.qualityControlApprovalsData.set(data);
    this.isValid.set(!!this.qualityControlApprovalsData()?.at(0)?.id);
    this.noFirstData.set(!!this.qualityControlApprovalsData()?.at(0)?.id);
    this.initForm();
  }

  private initForm(): void {
    if (!this.qualityControlApprovalsData().length) {
      this.handleAddNew();
      return;
    }

    const formGroups = this.qualityControlApprovalsData().map(data => {
      const formGroup = this.baseModalService.getFormGroup<QualityControlApprovalsFormField>(qualityControlApprovalsFormFields);
      formGroup.patchValue(data);
      return formGroup;
    });

    this.qualityControlApprovalsFormGroups.set(formGroups);
  }

  private removeFactoryBankDetailsAtIndex(index: number): void {
    this.qualityControlApprovalsFormGroups.update(groups => {
      const newGroups = [...groups];
      newGroups.splice(index, 1);
      return newGroups;
    });

    this.qualityControlApprovalsData.update(data => {
      const newData = [...data];
      newData.splice(index, 1);
      return newData;
    });

    if (!this.qualityControlApprovalsData().length) {
      this.noFirstData.set(false);
      this.handleAddNew();
    }

    this.isValid.set(!!this.qualityControlApprovalsData().length);
  }
}
